// src/components/DynamicForm/FormField.tsx

import * as React from "react";
import { FormField as FormFieldType, FieldSource } from "../../interfaces/formTypes";

/**
 * Props for the FormField component
 */
export interface FormFieldProps {
  field: FormFieldType;
  value: string;
  onChange: (id: string, value: string) => void;
  error?: string;
  disabled?: boolean;
}

/**
 * Source indicator badge colors
 */
const sourceColors: Record<FieldSource, { bg: string; text: string; label: string }> = {
  document: { bg: "#E3F2FD", text: "#1565C0", label: "From document" },
  conversation: { bg: "#E8F5E9", text: "#2E7D32", label: "From chat" },
  inferred: { bg: "#FFF3E0", text: "#E65100", label: "Inferred" },
  default: { bg: "#F5F5F5", text: "#757575", label: "" },
};

/**
 * Individual form field renderer
 * Renders different input types based on field.type
 */
const FormField: React.FC<FormFieldProps> = ({
  field,
  value,
  onChange,
  error,
  disabled = false,
}) => {
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    onChange(field.id, e.target.value);
  };

  // Determine width based on field.width
  const fieldWidth = field.width === "half" ? "calc(50% - 6px)" : "100%";

  // Container styles
  const containerStyle: React.CSSProperties = {
    display: "flex",
    flexDirection: "column",
    width: fieldWidth,
    marginBottom: "12px",
  };

  // Label styles
  const labelStyle: React.CSSProperties = {
    display: "flex",
    alignItems: "center",
    gap: "6px",
    fontSize: "13px",
    fontWeight: 500,
    color: "#333",
    marginBottom: "4px",
  };

  // Required indicator
  const requiredStyle: React.CSSProperties = {
    color: "#D32F2F",
    marginLeft: "2px",
  };

  // Source badge styles
  const sourceColor = sourceColors[field.source];
  const sourceBadgeStyle: React.CSSProperties = {
    display: field.source !== "default" ? "inline-block" : "none",
    fontSize: "10px",
    fontWeight: 500,
    padding: "2px 6px",
    borderRadius: "4px",
    backgroundColor: sourceColor.bg,
    color: sourceColor.text,
  };

  // Base input styles
  const inputBaseStyle: React.CSSProperties = {
    width: "100%",
    padding: "8px 10px",
    fontSize: "13px",
    fontFamily: "'Segoe UI', sans-serif",
    border: error ? "1px solid #D32F2F" : "1px solid #E0E0E0",
    borderRadius: "4px",
    backgroundColor: disabled ? "#FAFAFA" : "#FFFFFF",
    color: disabled ? "#9E9E9E" : "#333",
    outline: "none",
    transition: "border-color 0.2s, box-shadow 0.2s",
    boxSizing: "border-box",
  };

  // Textarea-specific styles
  const textareaStyle: React.CSSProperties = {
    ...inputBaseStyle,
    minHeight: "80px",
    resize: "vertical",
  };

  // Error message styles
  const errorStyle: React.CSSProperties = {
    fontSize: "11px",
    color: "#D32F2F",
    marginTop: "4px",
  };

  // Help text styles
  const helpTextStyle: React.CSSProperties = {
    fontSize: "11px",
    color: "#757575",
    marginTop: "4px",
  };

  // Focus handler for styling
  const handleFocus = (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    if (!disabled) {
      e.target.style.borderColor = "#1976D2";
      e.target.style.boxShadow = "0 0 0 2px rgba(25, 118, 210, 0.1)";
    }
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    e.target.style.borderColor = error ? "#D32F2F" : "#E0E0E0";
    e.target.style.boxShadow = "none";
  };

  // Render input based on type
  const renderInput = () => {
    const commonProps = {
      id: field.id,
      name: field.id,
      value: value,
      placeholder: field.placeholder,
      disabled: disabled,
      onChange: handleChange,
      onFocus: handleFocus,
      onBlur: handleBlur,
      "aria-invalid": !!error,
      "aria-describedby": error ? `${field.id}-error` : undefined,
    };

    switch (field.type) {
      case "textarea":
        return <textarea {...commonProps} style={textareaStyle} />;

      case "date":
        return <input {...commonProps} type="date" style={inputBaseStyle} />;

      case "email":
        return <input {...commonProps} type="email" style={inputBaseStyle} />;

      case "number":
        return <input {...commonProps} type="number" style={inputBaseStyle} />;

      case "text":
      default:
        return <input {...commonProps} type="text" style={inputBaseStyle} />;
    }
  };

  return (
    <div style={containerStyle}>
      <label htmlFor={field.id} style={labelStyle}>
        <span>
          {field.label}
          {field.required && <span style={requiredStyle}>*</span>}
        </span>
        {field.source !== "default" && <span style={sourceBadgeStyle}>{sourceColor.label}</span>}
      </label>

      {renderInput()}

      {error && (
        <span id={`${field.id}-error`} style={errorStyle} role="alert">
          {error}
        </span>
      )}

      {!error && field.helpText && <span style={helpTextStyle}>{field.helpText}</span>}
    </div>
  );
};

export default FormField;
